import React, { useEffect, useState } from 'react';
import { Archive, CheckCircle2, Loader2, Plus, RefreshCw } from 'lucide-react';
import {
  archiveManagedContent,
  createManagedContent,
  getManagedContent,
  publishManagedContent,
} from '../../services/api';

const initialForm = {
  content_type: 'service',
  title: '',
  slug: '',
  meta_title: '',
  meta_description: '',
  author: 'Mustafa Can Arslan, Founder of Webouse',
  sectionHeading: 'Overview',
  sectionBody: '',
  sitemap_inclusion: false,
  public_status: 'draft',
  client_visibility_permission: false,
};

const ContentManager = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');

  const loadContent = async () => {
    setIsLoading(true);
    setMessage('');
    try {
      setItems(await getManagedContent());
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Could not load content records.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadContent();
  }, []);

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const submitDraft = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setMessage('');
    try {
      const payload = {
        content_type: form.content_type,
        status: 'draft',
        title: form.title,
        slug: form.slug,
        meta_title: form.meta_title || form.title,
        meta_description: form.meta_description,
        author: form.author,
        sitemap_inclusion: form.sitemap_inclusion,
        public_status: form.public_status,
        client_visibility_permission: form.client_visibility_permission,
        content_sections: [
          {
            heading: form.sectionHeading || 'Overview',
            body: form.sectionBody,
            items: [],
          },
        ],
      };
      await createManagedContent(payload);
      setForm(initialForm);
      setMessage('Draft content record created.');
      await loadContent();
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Could not create content record.');
    } finally {
      setIsSaving(false);
    }
  };

  const publishItem = async (id) => {
    setMessage('');
    await publishManagedContent(id);
    setMessage('Content record published. Public visibility still depends on sitemap and case approval fields.');
    await loadContent();
  };

  const archiveItem = async (id) => {
    setMessage('');
    await archiveManagedContent(id);
    setMessage('Content record archived.');
    await loadContent();
  };

  return (
    <div className="space-y-6" data-testid="content-manager">
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
        <div className="mb-5 flex items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-white">Editorial Content Records</h2>
            <p className="mt-1 text-sm text-white/50">
              Manage service, resource and case-study records without mixing them with private demo access.
            </p>
          </div>
          <button
            type="button"
            onClick={loadContent}
            className="glass-icon-btn h-11 w-11"
            aria-label="Refresh content records"
          >
            <RefreshCw className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {message && (
          <div className="mb-5 rounded-xl border border-[#00FFD1]/25 bg-[#00FFD1]/10 px-4 py-3 text-sm text-[#00FFD1]">
            {message}
          </div>
        )}

        <form onSubmit={submitDraft} className="grid gap-4 lg:grid-cols-2">
          <label className="space-y-2 text-sm text-white/70">
            Content type
            <select
              value={form.content_type}
              onChange={(event) => updateField('content_type', event.target.value)}
              className="glass-input"
            >
              <option value="service">Service page</option>
              <option value="resource">Resource article</option>
              <option value="case_study">Case study</option>
            </select>
          </label>

          <label className="space-y-2 text-sm text-white/70">
            Slug
            <input
              value={form.slug}
              onChange={(event) => updateField('slug', event.target.value)}
              required
              pattern="[a-z0-9]+(-[a-z0-9]+)*"
              className="glass-input"
              placeholder="example-service-page"
            />
          </label>

          <label className="space-y-2 text-sm text-white/70">
            Title
            <input
              value={form.title}
              onChange={(event) => updateField('title', event.target.value)}
              required
              className="glass-input"
              placeholder="Content title"
            />
          </label>

          <label className="space-y-2 text-sm text-white/70">
            Meta title
            <input
              value={form.meta_title}
              onChange={(event) => updateField('meta_title', event.target.value)}
              className="glass-input"
              placeholder="Optional meta title"
            />
          </label>

          <label className="space-y-2 text-sm text-white/70 lg:col-span-2">
            Meta description
            <textarea
              value={form.meta_description}
              onChange={(event) => updateField('meta_description', event.target.value)}
              required
              rows={2}
              className="glass-input resize-none"
              placeholder="Short public search description"
            />
          </label>

          <label className="space-y-2 text-sm text-white/70">
            Section heading
            <input
              value={form.sectionHeading}
              onChange={(event) => updateField('sectionHeading', event.target.value)}
              className="glass-input"
            />
          </label>

          <label className="space-y-2 text-sm text-white/70">
            Author
            <input
              value={form.author}
              onChange={(event) => updateField('author', event.target.value)}
              className="glass-input"
            />
          </label>

          <label className="space-y-2 text-sm text-white/70 lg:col-span-2">
            Section body
            <textarea
              value={form.sectionBody}
              onChange={(event) => updateField('sectionBody', event.target.value)}
              rows={4}
              className="glass-input resize-none"
              placeholder="Visible editorial content for review"
            />
          </label>

          {form.content_type === 'case_study' && (
            <div className="lg:col-span-2 grid gap-3 rounded-xl border border-white/10 bg-black/30 p-4 md:grid-cols-2">
              <label className="flex items-center gap-3 text-sm text-white/70">
                <input
                  type="checkbox"
                  checked={form.client_visibility_permission}
                  onChange={(event) => updateField('client_visibility_permission', event.target.checked)}
                />
                Client visibility permission confirmed
              </label>
              <label className="space-y-2 text-sm text-white/70">
                Public status
                <select
                  value={form.public_status}
                  onChange={(event) => updateField('public_status', event.target.value)}
                  className="glass-input"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>
              </label>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-4 lg:col-span-2">
            <label className="flex items-center gap-3 text-sm text-white/70">
              <input
                type="checkbox"
                checked={form.sitemap_inclusion}
                onChange={(event) => updateField('sitemap_inclusion', event.target.checked)}
              />
              Include in sitemap when published
            </label>
            <button type="submit" disabled={isSaving} className="btn-glass-primary">
              {isSaving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Plus className="h-5 w-5" />}
              Create draft
            </button>
          </div>
        </form>
      </div>

      <div className="space-y-3">
        {isLoading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-8 w-8 animate-spin text-[#00FFD1]" />
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-8 text-center text-white/50">
            No managed content records yet.
          </div>
        ) : (
          items.map((item) => (
            <div
              key={item.id}
              className="rounded-2xl border border-white/10 bg-white/[0.03] p-5"
            >
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="text-xs uppercase text-[#00FFD1]">{item.content_type}</p>
                  <h3 className="mt-1 text-lg font-semibold text-white">{item.title}</h3>
                  <p className="mt-1 text-sm text-white/50">
                    /{item.content_type === 'case_study' ? 'work' : `${item.content_type}s`}/{item.slug}
                  </p>
                  <p className="mt-2 text-xs text-white/40">
                    Status: {item.status} | Sitemap: {item.sitemap_inclusion ? 'yes' : 'no'}
                    {item.content_type === 'case_study'
                      ? ` | Client permission: ${item.client_visibility_permission ? 'yes' : 'no'}`
                      : ''}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => publishItem(item.id)}
                    className="inline-flex items-center gap-2 rounded-xl border border-[#00FFD1]/25 bg-[#00FFD1]/10 px-4 py-2 text-sm font-semibold text-[#00FFD1]"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    Publish
                  </button>
                  <button
                    type="button"
                    onClick={() => archiveItem(item.id)}
                    className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.05] px-4 py-2 text-sm font-semibold text-white/70"
                  >
                    <Archive className="h-4 w-4" />
                    Archive
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ContentManager;
